import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Heart, Compass, TrendingUp } from 'lucide-react';
import PersonalizedSection from './PersonalizedSection';
import AdaptiveFilters from './AdaptiveFilters';
import { FLAGS } from '../../lib/flags';

/**
 * PersonalizedDiscovery - Main component for All tab with 4 intelligent sections
 */
export default function PersonalizedDiscovery({ onItemClick }) {
  const navigate = useNavigate();
  const [sectionItems, setSectionItems] = React.useState({
    forYou: [],
    liked: [],
    continue: [],
    trending: [],
  });

  const handleSeeAll = (sectionKey) => {
    // Navigate to search with appropriate filters
    const searchParams = new URLSearchParams();
    searchParams.set('section', sectionKey);
    navigate(`/search?${searchParams.toString()}`);
  };

  const handleFiltersChange = (filters) => {
    // Refetch sections with new filters
    // This would trigger a refetch in each PersonalizedSection
    window.dispatchEvent(new CustomEvent('refetchRecommendations', { detail: filters }));
  };

  return (
    <main className="space-y-10">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 mb-1">Discover</h1>
        <p className="text-sm text-neutral-600">
          Personalized content based on your interests and activity
        </p>
      </div>

      {/* Adaptive Filters - will be populated by section items */}
      <AdaptiveFilters
        items={Object.values(sectionItems).flat()}
        maxFilters={8}
        onFiltersChange={handleFiltersChange}
      />

      {/* For You Section */}
      <section className="border-b border-neutral-200/40 pb-10">
        <PersonalizedSection
          title="For You"
          description="Curated from your recent interests"
          icon={Sparkles}
          sectionKey="forYou"
          pageSize={20}
          onItemClick={onItemClick}
          onSeeAll={handleSeeAll}
          onItemsLoaded={(items) => setSectionItems(prev => ({ ...prev, forYou: items }))}
        />
      </section>

      {/* Because You Liked Section */}
      <section className="border-b border-neutral-200/40 pb-10">
        <PersonalizedSection
          title="Because You Liked"
          description="Similar to what you've saved and explored"
          icon={Heart}
          sectionKey="liked"
          pageSize={20}
          onItemClick={onItemClick}
          onSeeAll={handleSeeAll}
          onItemsLoaded={(items) => setSectionItems(prev => ({ ...prev, liked: items }))}
        />
      </section>

      {/* Continue Exploring Section */}
      <section className="border-b border-neutral-200/40 pb-10">
        <PersonalizedSection
          title="Continue Exploring"
          description="Pick up where you left off"
          icon={Compass}
          sectionKey="continue"
          pageSize={20}
          onItemClick={onItemClick}
          onSeeAll={handleSeeAll}
          onItemsLoaded={(items) => setSectionItems(prev => ({ ...prev, continue: items }))}
        />
      </section>

      {/* Trending Section (if enabled) */}
      {FLAGS.FEATURE_SECTION_TRENDING && (
        <section className="border-b border-neutral-200/40 pb-10">
          <PersonalizedSection
            title="Trending in Your World"
            description="Popular across your favorite topics"
            icon={TrendingUp}
            sectionKey="trending"
            pageSize={20}
            onItemClick={onItemClick}
            onSeeAll={handleSeeAll}
            onItemsLoaded={(items) => setSectionItems(prev => ({ ...prev, trending: items }))}
          />
        </section>
      )}
    </main>
  );
}

